import { Link } from "react-router-dom";
import { Bookmark, Clock } from "lucide-react";
import { FALLBACK_IMG, articlePath, imgUrl, fileUrl, timeAgo, catName } from "@/lib/api";
import { useSaved } from "@/hooks/useSaved";

export const Img = ({ src, fallback, className = "", alt = "" }) => (
  <img src={src || fallback || FALLBACK_IMG} alt={alt} loading="lazy" referrerPolicy="no-referrer"
    onError={(e) => {
      const el = e.currentTarget;
      const next = fallback && el.src !== fallback ? fallback : FALLBACK_IMG;
      if (el.src !== next) el.src = next;
    }}
    className={`h-full w-full object-cover ${className}`} />
);

export const BrandImg = ({ a, w = 640, className = "" }) => (
  <Img src={imgUrl(a.id, w)} fallback={fileUrl(a.image)} alt={a.title} className={className} />
);

export const TimeTag = ({ a, light }) => (
  <span className={`inline-flex items-center gap-1 font-mono-meta text-[10.5px] font-bold uppercase tracking-wider ${light ? "text-white/90" : "text-primary"}`}>
    <Clock size={11} />{timeAgo(a.date)}
  </span>
);

export const Meta = ({ a, light }) => (
  <div className={`flex flex-wrap items-center gap-x-2 font-mono-meta text-[10.5px] uppercase tracking-wider ${light ? "text-white/80" : "text-muted-foreground"}`}>
    <TimeTag a={a} light={light} /><span>·</span><span className="font-bold">{a.source}</span>
  </div>
);

function SaveBtn({ a }) {
  const { isSaved, toggle } = useSaved();
  const on = isSaved(a.id);
  return (
    <button data-testid={`card-save-btn-${a.id}`} aria-label="Guardar" onClick={(e) => { e.preventDefault(); toggle(a); }}
      className={`absolute bottom-2 right-2 grid h-8 w-8 place-items-center rounded-full backdrop-blur-md transition-transform hover:scale-110 ${on ? "bg-primary text-white" : "bg-black/50 text-white"}`}>
      <Bookmark size={14} fill={on ? "currentColor" : "none"} />
    </button>
  );
}

export function NewsCard({ a, i = 0 }) {
  return (
    <Link to={articlePath(a)} data-testid={`news-card-${a.id}`} className="group rise block" style={{ animationDelay: `${(i % 12) * 40}ms` }}>
      <div className="relative aspect-video overflow-hidden rounded-lg bg-muted">
        <BrandImg a={a} className="img-zoom" />
        <SaveBtn a={a} />
      </div>
      <div className="pt-3">
        <Meta a={a} />
        <h3 className="mt-1.5 font-serif-display text-[17px] font-bold leading-snug transition-colors group-hover:text-primary line-clamp-3">{a.title}</h3>
      </div>
    </Link>
  );
}

export function NewsRow({ a, n }) {
  return (
    <Link to={articlePath(a)} data-testid={`news-row-${a.id}`} className="group flex gap-3 py-3.5">
      {n != null && <span className="w-7 font-serif-display text-3xl font-black leading-none text-primary">{n}</span>}
      <div className="min-w-0 flex-1">
        <span className="font-mono-meta text-[10px] font-bold uppercase tracking-wider text-primary">{catName(a.category)}</span>
        <h4 className="mt-0.5 font-serif-display text-[15px] font-bold leading-snug group-hover:text-primary line-clamp-3">{a.title}</h4>
        <div className="mt-1"><TimeTag a={a} /></div>
      </div>
      <div className="aspect-video w-28 shrink-0 self-start overflow-hidden rounded-md bg-muted"><BrandImg a={a} w={320} className="img-zoom" /></div>
    </Link>
  );
}
